import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToastStatus = 'success' | 'fail' | 'error' | 'info';

export interface ToastMessage {
  status: ToastStatus;
  msg: string;
}

/**
 * Global toast / message popup service.
 *
 * Components push messages through `success()` / `error()` / `info()` /
 * `fail()`; the `MsgPopupComponent` subscribes to `messages$` and renders
 * the toast.
 *
 * Note: the project previously used the string `'fail'` for failure and
 * `'error'` for validation errors. Both are kept as a single `ToastStatus`
 * union so existing call-sites (`dataService.showMsg('fail', '…')`,
 * `dataService.showMsg('error', '…')`) keep working unchanged.
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly _messages = new BehaviorSubject<ToastMessage | null>(null);
  readonly messages$: Observable<ToastMessage | null> = this._messages.asObservable();

  show(status: ToastStatus, msg: string): void {
    this._messages.next({ status, msg });
  }

  success(msg: string): void {
    this.show('success', msg);
  }

  fail(msg: string): void {
    this.show('fail', msg);
  }

  error(msg: string): void {
    this.show('error', msg);
  }

  info(msg: string): void {
    this.show('info', msg);
  }
}
